import { useCallback, useEffect, useState } from "react";
import { theme, type as text } from "../theme";
import { api } from "../api/client";
import { frameworkAccent } from "../components/controlStatus";
import { DetailSection } from "../components/RecordView";
import { StandardHeader, StandardNote, STANDARD_PAGE_LABELS } from "../components/standards/StandardHeader";
import { ControlGroup, ControlGroupStack } from "../components/standards/ControlGroup";
import { SummaryBar } from "../components/standards/StatusSummary";
import { MaturityPicker, MaturityPill, type MaturityLevel } from "../components/standards/MaturityScale";
import { useCurrentUser } from "../auth/useCurrentUser";
import { MaturityJustification, MaturityTargetSettings, type MaturityScaleOption, type MaturityTargetRow } from "./NistCsf";

// IWA 48:2024 ESG implementation principles: a guidance document, not a certifiable standard, so
// each of the seven principles carries a self-assessed maturity level (stored per organisation,
// PUT /maturity/iwa48/:itemId) rather than a status. The principles sit in the same collapsible
// groups as every other standard's clauses.

const PRINCIPLES: Array<{
  id: string;
  num: number;
  title: string;
  subtitle: string;
  description: string;
  indicators: string[];
  relatedStandards: string[];
}> = [
  {
    id: "p1",
    num: 1,
    title: "Purpose and Commitment",
    subtitle: "Define your ESG purpose and obtain top-level commitment",
    description: "Organisations should articulate a clear ESG purpose that reflects their values and the role they play in sustainable development. Leadership commitment — evidenced by board-level ownership and integration into strategic planning — is the foundation of credible ESG implementation.",
    indicators: [
      "Board or executive-level ESG policy or commitment statement published",
      "ESG objectives integrated into the organisation's strategic plan",
      "Named executive or board member accountable for ESG",
      "ESG purpose communicated internally to all staff",
    ],
    relatedStandards: ["ISO 14001 §5.1", "ISO 45001 §5.1", "ISO 9001 §5.1", "GRI 2-9", "TCFD Governance"],
  },
  {
    id: "p2",
    num: 2,
    title: "Governance",
    subtitle: "Establish clear ESG governance structures and accountability",
    description: "Effective ESG governance requires clear ownership, defined roles and responsibilities, integration with existing risk management and audit functions, and mechanisms for oversight and accountability. Governance structures should prevent greenwashing and ensure ESG commitments translate into action.",
    indicators: [
      "ESG governance structure documented (board committee, ESG steering group, or equivalent)",
      "ESG risks and opportunities integrated into enterprise risk management",
      "Internal audit or assurance coverage of ESG disclosures",
      "Anti-greenwashing controls in place for external ESG communications",
    ],
    relatedStandards: ["ISO 37001", "ISO 37301", "GRI 2-12", "TCFD Governance", "ISSB IFRS S1"],
  },
  {
    id: "p3",
    num: 3,
    title: "Stakeholder Engagement",
    subtitle: "Identify and engage meaningfully with relevant stakeholders",
    description: "Understanding the needs and expectations of stakeholders — including employees, investors, customers, suppliers, regulators, and communities — is essential for setting credible ESG priorities and maintaining social licence to operate. Engagement should be two-way and documented.",
    indicators: [
      "Stakeholder map identifying key ESG-relevant parties",
      "Stakeholder engagement process documented with frequency and methods",
      "Stakeholder concerns and expectations captured and used to inform ESG priorities",
      "Responses to stakeholder enquiries on ESG topics tracked and recorded",
    ],
    relatedStandards: ["ISO 14001 §4.2", "ISO 45001 §4.2", "GRI 2-29", "AA1000 SES", "TCFD Strategy"],
  },
  {
    id: "p4",
    num: 4,
    title: "Materiality Assessment",
    subtitle: "Identify the ESG topics most significant to your organisation and stakeholders",
    description: "A materiality assessment determines which ESG topics are most significant — both in terms of their impact on the business (financial materiality) and the organisation's impact on people and the planet (impact materiality). Double materiality is increasingly required by regulators and investors. Results should be reviewed periodically and used to prioritise ESG effort.",
    indicators: [
      "Materiality assessment methodology documented",
      "Most recent materiality assessment completed within the last 3 years",
      "Material topics identified cover environmental, social, and governance dimensions",
      "Materiality matrix or results published in ESG/sustainability report",
    ],
    relatedStandards: ["GRI 3", "ESRS CSRD", "ISSB IFRS S1", "SASB", "TCFD Strategy"],
  },
  {
    id: "p5",
    num: 5,
    title: "Transparency and Disclosure",
    subtitle: "Report ESG performance openly and consistently",
    description: "Transparent and consistent disclosure builds stakeholder trust, supports comparability, and demonstrates accountability. Disclosures should be based on material topics, use recognised frameworks or standards, be subject to appropriate review, and — where possible — third-party assurance.",
    indicators: [
      "ESG or sustainability report published (at minimum annually)",
      "Disclosure aligned to a recognised standard (GRI, TCFD, ISSB, CSRD, CDP, B Corp, etc.)",
      "Data disclosed covers all material ESG topics identified in the materiality assessment",
      "Third-party assurance obtained on key ESG metrics (limited or reasonable assurance)",
    ],
    relatedStandards: ["GRI Standards", "TCFD", "ISSB IFRS S1/S2", "CSRD ESRS", "CDP Questionnaire"],
  },
  {
    id: "p6",
    num: 6,
    title: "Performance Management",
    subtitle: "Set targets, measure progress, and drive continual improvement",
    description: "ESG commitments must be backed by measurable targets, clear KPIs, and a process for monitoring and reporting progress. Management reviews should assess performance against targets, identify gaps, and drive corrective action. Improvement should be evidenced over time.",
    indicators: [
      "ESG KPIs defined covering environmental, social, and governance dimensions",
      "Baseline data established and targets set for key ESG metrics",
      "ESG performance reviewed by management at least annually",
      "Improvement trajectory evidenced year-on-year against at least one material KPI",
    ],
    relatedStandards: ["ISO 14001 §9.1", "ISO 45001 §9.1", "ISO 9001 §9.1", "GRI 305 (Emissions)", "TCFD Metrics & Targets"],
  },
  {
    id: "p7",
    num: 7,
    title: "Integration",
    subtitle: "Embed ESG across the organisation's management systems and value chain",
    description: "ESG should not be a standalone exercise. Integration with existing management systems (ISO 14001, ISO 45001, ISO 9001, ISO 27001), procurement and supply chain processes, product and service design, and human capital management ensures that sustainability becomes part of how the organisation operates day to day.",
    indicators: [
      "ESG considerations integrated into procurement and supplier selection",
      "Relevant ISO management system standards enrolled and aligned to ESG objectives",
      "Employee ESG awareness and training programme in place",
      "ESG criteria included in new product/service development or investment decisions",
    ],
    relatedStandards: ["ISO 14001", "ISO 45001", "ISO 9001", "ISO 26000", "UN SDGs"],
  },
];

// The levels used to live only in this browser's localStorage. They are now per organisation
// (PUT /maturity/iwa48/:itemId). The key is read once to migrate whatever a browser still holds,
// then cleared.
const LEGACY_STORAGE_KEY = "iwa48_maturity_v1";

function legacyMaturity(): Record<string, number> {
  try { return JSON.parse(localStorage.getItem(LEGACY_STORAGE_KEY) ?? "{}") as Record<string, number>; } catch { return {}; }
}

function clearLegacyMaturity(): void {
  try { localStorage.removeItem(LEGACY_STORAGE_KEY); } catch { /* private window — nothing to clear */ }
}

interface MaturityPayload {
  /** The scale in force (backend lib/maturityScales.ts); its levels are the ones the server accepts. */
  scale?: { key: string; name: string; levels: MaturityLevel[] };
  scales?: MaturityScaleOption[];
  entries: Array<{ itemId: string; level: number; justification?: string | null }>;
  target?: MaturityTargetRow | null;
  summary: { average: number | null; assessed: number; total: number; target: number | null };
}

// Mirror of IWA48_MATURITY in backend/src/lib/maturityScales.ts, shown only until
// GET /maturity/iwa48 returns the scale in force — keep the two identical.
const MATURITY_LEVELS: MaturityLevel[] = [
  { value: 1, label: "Ad hoc", description: "No defined approach; activity happens case by case." },
  { value: 2, label: "Emerging", description: "An approach exists in places but is neither documented nor consistent." },
  { value: 3, label: "Defined", description: "A documented approach is in place and applied across the organisation." },
  { value: 4, label: "Managed", description: "The approach is measured, reviewed and held to objectives." },
  { value: 5, label: "Leading", description: "The approach is continually improved and informs others in the sector." },
];

export function Iwa48Page() {
  const accent = frameworkAccent("iwa48");
  const { user } = useCurrentUser();
  const canEdit = !!user?.permissions?.includes("controls:write");
  const [justifications, setJustifications] = useState<Record<string, string>>({});
  const [scaleInfo, setScaleInfo] = useState<{ key: string; scales: MaturityScaleOption[]; target: MaturityTargetRow | null } | null>(null);
  const [maturity, setMaturity] = useState<Record<string, number>>({});
  const [summary, setSummary] = useState<MaturityPayload["summary"] | null>(null);
  const [levels, setLevels] = useState<MaturityLevel[]>(MATURITY_LEVELS);
  const [saveError, setSaveError] = useState("");
  const [expanded, setExpanded] = useState<Set<string>>(new Set(["p1"]));

  const applyPayload = useCallback((d: MaturityPayload) => {
    setMaturity(Object.fromEntries(d.entries.filter((e) => e.level > 0).map((e) => [e.itemId, e.level])));
    setSummary(d.summary);
    if (d.scale?.levels?.length) setLevels(d.scale.levels);
    setJustifications(Object.fromEntries(d.entries.map((e) => [e.itemId, e.justification ?? ""])));
    if (d.scale) setScaleInfo({ key: d.scale.key, scales: d.scales ?? [d.scale], target: d.target ?? null });
  }, []);
  const reload = useCallback(() => { api.get<MaturityPayload>("/maturity/iwa48").then(applyPayload).catch(() => {}); }, [applyPayload]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const legacy = legacyMaturity();
        if (Object.keys(legacy).length > 0) {
          await api.post("/maturity/iwa48/import", { levels: legacy }).catch(() => {});
          clearLegacyMaturity();
        }
        const d = await api.get<MaturityPayload>("/maturity/iwa48");
        if (!cancelled) applyPayload(d);
      } catch { /* the page still reads as a reference guide without the assessment */ }
    })();
    return () => { cancelled = true; };
  }, [applyPayload]);

  async function setLevel(id: string, level: number) {
    const previous = maturity;
    setMaturity((prev) => ({ ...prev, [id]: level }));
    setSaveError("");
    try {
      await api.put(`/maturity/iwa48/${id}`, { level });
      applyPayload(await api.get<MaturityPayload>("/maturity/iwa48"));
    } catch {
      setMaturity(previous);
      setSaveError("Could not save that level. Check your connection and try again.");
    }
  }

  // The server averages only the principles actually assessed, so three at level 4 reads as 4.0
  // on what has been looked at rather than 1.7 overall.
  const atLevel3Plus = PRINCIPLES.filter((p) => (maturity[p.id] ?? 0) >= 3).length;
  const assessed = summary?.assessed ?? PRINCIPLES.filter((p) => (maturity[p.id] ?? 0) > 0).length;

  const toggle = (id: string) => setExpanded((prev) => { const n = new Set(prev); if (n.has(id)) n.delete(id); else n.add(id); return n; });

  return (
    <div className="page-pad">
      <StandardHeader
        frameworkId="iwa48"
        page={STANDARD_PAGE_LABELS.esgPrinciples}
        subtitle="Self-assess the organisation's maturity against each of the seven ESG implementation principles."
      />

      <SummaryBar
        label="Principles assessed"
        done={assessed}
        total={PRINCIPLES.length}
        cells={[
          // A maturity level is never a percentage, and nothing assessed has no average (docs/MATURITY-ASSESSMENT.md).
          { key: "avg", value: summary?.average != null ? `${summary.average.toFixed(1)} / 5` : "—", label: "Average maturity" },
          { key: "defined", value: `${atLevel3Plus}/${PRINCIPLES.length}`, label: "At Defined or above" },
        ]}
      />
      {saveError && <div style={{ ...text.hint, color: theme.color.dangerText, marginBottom: 12 }}>{saveError}</div>}
      {scaleInfo && (
        <MaturityTargetSettings frameworkId="iwa48" scales={scaleInfo.scales} scaleKey={scaleInfo.key} target={scaleInfo.target}
          levelWord="Level" canEdit={canEdit} onSaved={reload} />
      )}

      <ControlGroupStack>
        {PRINCIPLES.map((p) => {
          const level = maturity[p.id] ?? 0;
          const isOpen = expanded.has(p.id);
          return (
            <ControlGroup
              key={p.id}
              title={<><span style={{ ...text.ref, fontSize: "inherit", marginRight: 8 }}>{p.num}</span>{p.title}</>}
              hint={p.subtitle}
              accent={accent}
              count={p.indicators.length}
              noun="indicator"
              extra={<MaturityPill level={level} levels={levels} />}
              expanded={isOpen}
              onToggle={() => toggle(p.id)}
            >
              <div style={{ padding: "0 16px 14px", borderTop: `1px solid ${theme.color.border}` }}>
                <DetailSection title="Principle">
                  <div style={text.body}>{p.description}</div>
                </DetailSection>
                <DetailSection title="Maturity">
                  <MaturityPicker label="Set the maturity level" level={level} levels={levels} onPick={(v) => setLevel(p.id, v)} />
                  <div style={{ marginTop: 10 }}>
                    <div style={{ ...text.label, marginBottom: 4 }}>Justification</div>
                    <MaturityJustification frameworkId="iwa48" itemId={p.id} value={justifications[p.id] ?? ""} canEdit={canEdit} onSaved={reload} />
                  </div>
                </DetailSection>
                <DetailSection title="Evidence indicators">
                  <ul style={{ margin: 0, paddingLeft: 18, display: "flex", flexDirection: "column", gap: 3 }}>
                    {p.indicators.map((ind) => <li key={ind} style={text.hint}>{ind}</li>)}
                  </ul>
                </DetailSection>
                <DetailSection title="Related standards and frameworks">
                  <div style={{ display: "flex", gap: 5, flexWrap: "wrap" }}>
                    {p.relatedStandards.map((s) => (
                      <span key={s} style={{ ...text.meta, fontWeight: 600, background: theme.color.accentBg, color: theme.color.accentText, border: `1px solid ${theme.color.border}`, borderRadius: 3, padding: "2px 8px" }}>{s}</span>
                    ))}
                  </div>
                </DetailSection>
              </div>
            </ControlGroup>
          );
        })}
      </ControlGroupStack>

      <StandardNote title="About IWA 48:2024">
        IWA 48:2024 is an ISO International Workshop Agreement published in November 2024 and launched at COP29. It is a
        free-to-access guidance document, not a certifiable standard, that provides a high-level framework for embedding
        environmental, social and governance (ESG) principles across organisations of any size and sector. It is designed
        to complement the ISO management system standards (ISO 14001, ISO 45001, ISO 9001, ISO 27001) and established
        ESG reporting frameworks (GRI, TCFD, ISSB, CSRD). This assessment is a self-evaluation; the maturity levels are
        kept for the organisation, so everyone in it sees the same answers.
      </StandardNote>
    </div>
  );
}
